import * as Yup from "yup";
import { AsyncSelectOption } from "@/components/AsyncSelect";
import { UpdatePriorAuthorizationStatusStatusEnum } from "@/services";
import { COMPONENT } from "@/tokens/design-tokens";

export type AuthorizationForm = {
  authorizationNumber: string;
  payerId: AsyncSelectOption | null;
  startDate: Date | null;
  endDate: Date | null;
  requestingProvider: AsyncSelectOption | null;
  allowedVisit: string;
  warningCount: string;
  remainingVisit: string;
  cptCodes: AsyncSelectOption[] | null;
  notesComments: string;
  attachmentUpload: File | null;
  fileName: string;
  linkedEncounter: AsyncSelectOption | null;
  patientName?: string;
  patientId?: string;
};

export const VISIT_COUNT_MIN = 0;
export const VISIT_COUNT_MAX = 999;

export const AuthorizationValidationSchema = Yup.object().shape({
  authorizationNumber: Yup.string()
    .trim()
    .required("Authorization Number is required"),

  payerId: Yup.object()
    .nullable()
    .required("Payer Name is required")
    .test(
      "is-selected",
      "Please select a payer",
      (value: any) => !!value?.value
    ),

  startDate: Yup.date()
    .nullable()
    .required("Start Date is required")
    .typeError("Please enter a valid start date"),

  endDate: Yup.date()
    .nullable()
    .required("End Date is required")
    .typeError("Please enter a valid end date")
    .when("startDate", (startDate, schema) =>
      startDate?.[0]
        ? schema.min(startDate[0], "End Date must be on or after Start Date")
        : schema
    ),

  cptCodes: Yup.array()
    .of(Yup.object())
    .min(1, "At least one CPT Code is required")
    .required("CPT Code is required"),

  requestingProvider: Yup.object()
    .nullable()
    .required("Requesting Provider is required")
    .test(
      "is-selected",
      "Please select a provider",
      (value: any) => !!value?.value
    ),

  allowedVisit: Yup.number()
    .typeError("Allowed Visit must be a number")
    .required("Allowed Visit is required")
    .integer("Allowed Visit must be a whole number")
    .min(1, `Allowed Visit must be at least 1`)
    .max(VISIT_COUNT_MAX, `Allowed Visit cannot exceed ${VISIT_COUNT_MAX}`),

  warningCount: Yup.number()
    .typeError("Warning Count must be a number")
    .required("Warning Count is required")
    .integer("Warning Count must be a whole number")
    .min(VISIT_COUNT_MIN, "Warning Count cannot be negative")
    .max(Yup.ref("allowedVisit"), "Warning Count cannot exceed Allowed Visit"),

  remainingVisit: Yup.number()
    .typeError("Remaining Visit must be a number")
    .required("Remaining Visit is required")
    .integer("Remaining Visit must be a whole number")
    .min(VISIT_COUNT_MIN, "Remaining Visit cannot be negative")
    .max(
      Yup.ref("allowedVisit"),
      "Remaining Visit cannot exceed Allowed Visit"
    ),

  notesComments: Yup.string().max(1000, "Notes cannot exceed 1000 characters"),

  attachmentUpload: Yup.mixed().nullable(),

  linkedEncounter: Yup.object().nullable(),
});

export const AUTHORIZATION_FORM_INITIAL_VALUES: AuthorizationForm = {
  authorizationNumber: "",
  fileName: "",
  payerId: null,
  startDate: new Date(),
  endDate: null,
  requestingProvider: null,
  allowedVisit: "",
  warningCount: "",
  remainingVisit: "",
  cptCodes: [],
  notesComments: "",
  attachmentUpload: null,
  linkedEncounter: null,
};

// Status colors pulled from design-tokens.ts (Figma: Badge/Text + Badge/Bg)
// Status is derived from the End Date, so only these two remain.
export const STATUS_COLOR_MAP: Record<
  string,
  { color: string; bgColor: string }
> = {
  ACTIVE: {
    color: COMPONENT.badge.text.positive,
    bgColor: COMPONENT.badge.bg.positive,
  },
  INACTIVE: {
    color: COMPONENT.badge.text.negative,
    bgColor: COMPONENT.badge.bg.negative,
  },
};

export const AUTHORIZATION_STATUS = {
  ACTIVE: UpdatePriorAuthorizationStatusStatusEnum.Active,
  INACTIVE: UpdatePriorAuthorizationStatusStatusEnum.Inactive,
} as const;

export type AuthorizationStatus =
  (typeof AUTHORIZATION_STATUS)[keyof typeof AUTHORIZATION_STATUS];

export const AUTHORIZATION_ACTION = {
  VIEW: "view",
  EDIT: "edit",
  ARCHIVE: "archive",
} as const;

export const AUTHORIZATION_ACTION_OPTION = {
  VIEW: { value: AUTHORIZATION_ACTION.VIEW, label: "View" },
  EDIT: { value: AUTHORIZATION_ACTION.EDIT, label: "Edit" },
  ARCHIVE: { value: AUTHORIZATION_ACTION.ARCHIVE, label: "Archive" },
  UNARCHIVE: { value: AUTHORIZATION_ACTION.ARCHIVE, label: "Unarchive" },
} as const;

export const AUTHORIZATION_LIST_QUERY_KEY = "authorization-list";

export const SHOW_INACTIVE_LABEL = "Show Inactive";
export const SHOW_ARCHIVED_LABEL = "Show Archived";
